// src/pages/GlossaryFaqPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './GlossaryFaqPage.css';
import metricDefinitions from '../constants/metricsDefinitions';

function stripHtmlToText(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function paragraphsFor(key) {
  const raw = metricDefinitions?.[key] || '';
  const text = stripHtmlToText(raw);
  if (!text) return [];
  return text
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean);
}

const GLOSSARY = [
  {
    title: 'Liquidity',
    items: [
      { key: 'currentRatio', label: 'Current ratio' },
      { key: 'quickRatio', label: 'Quick ratio' },
      { key: 'cashRatio', label: 'Cash ratio' },
    ],
  },
  {
    title: 'Profitability',
    items: [
      { key: 'grossProfitMargin', label: 'Gross profit margin' },
      { key: 'operatingProfitMargin', label: 'Operating profit margin' },
      { key: 'netProfitMargin', label: 'Net profit margin' },
      { key: 'ebitMargin', label: 'EBIT margin' },
      { key: 'ebitdaMargin', label: 'EBITDA margin' },
    ],
  },
  {
    title: 'Efficiency / operations',
    items: [
      { key: 'assetTurnover', label: 'Asset turnover' },
      { key: 'inventoryTurnover', label: 'Inventory turnover' },
      { key: 'receivablesTurnover', label: 'Receivables turnover' },
      { key: 'payablesTurnover', label: 'Payables turnover' },
      { key: 'fixedAssetTurnover', label: 'Fixed asset turnover' },
    ],
  },
  {
    title: 'Risk & leverage',
    items: [
      { key: 'debtToAssetsRatio', label: 'Debt-to-assets ratio' },
      { key: 'debtToEquityRatio', label: 'Debt-to-equity ratio' },
      { key: 'debtToCapitalRatio', label: 'Debt-to-capital ratio' },
      { key: 'longTermDebtToCapitalRatio', label: 'Long-term debt-to-capital ratio' },
      { key: 'financialLeverageRatio', label: 'Financial leverage ratio' },
    ],
  },
  {
    title: 'Cash flow',
    items: [
      { key: 'operatingCashFlowRatio', label: 'Operating cash flow ratio' },
      { key: 'operatingCashFlowSalesRatio', label: 'Operating cash flow / sales' },
      { key: 'freeCashFlowOperatingCashFlowRatio', label: 'Free cash flow / operating cash flow' },
      { key: 'operatingCashFlowPerShare', label: 'Operating cash flow per share' },
      { key: 'freeCashFlowPerShare', label: 'Free cash flow per share' },
    ],
  },
  {
    title: 'Valuation',
    items: [
      { key: 'priceToEarningsRatio', label: 'Price-to-earnings (P/E)' },
      { key: 'priceToSalesRatio', label: 'Price-to-sales (P/S)' },
      { key: 'priceToFreeCashFlowRatio', label: 'Price-to-free-cash-flow' },
      { key: 'priceToOperatingCashFlowRatio', label: 'Price-to-operating-cash-flow' },
      { key: 'priceToBookRatio', label: 'Price-to-book (P/B)' },
      { key: 'enterpriseValueMultiple', label: 'EV/EBITDA' },
    ],
  },
  {
    title: 'Per-share metrics',
    items: [
      { key: 'revenuePerShare', label: 'Revenue per share' },
      { key: 'netIncomePerShare', label: 'Net income per share (EPS)' },
      { key: 'cashPerShare', label: 'Cash per share' },
      { key: 'bookValuePerShare', label: 'Book value per share' },
      { key: 'tangibleBookValuePerShare', label: 'Tangible book value per share' },
      { key: 'shareholdersEquityPerShare', label: "Shareholders' equity per share" },
    ],
  },
];

export default function GlossaryFaqPage() {
  return (
    <main className="glossary-faq">
      <div className="glossary-faq-card">
        <h1 className="glossary-faq-title">Glossary & FAQ</h1>
        <p className="glossary-faq-subtitle">
          Plain-English definitions of common stock metrics, plus answers to common questions about
          how to use StockVisualize.
        </p>

        <nav className="glossary-faq-nav">
          <a href="#glossary">Glossary</a>
          <a href="#faq">FAQ</a>
          <Link to="/">Back to Home</Link>
        </nav>

        <section id="glossary" className="glossary-faq-section" aria-labelledby="glossary-title">
          <h2 id="glossary-title">Glossary</h2>
          <p className="glossary-faq-muted">
            Definitions are shown as normal page text (not tooltips), so they’re easy to read and
            crawl.
          </p>

          {GLOSSARY.map((group) => (
            <div className="glossary-group" key={group.title}>
              <h3 className="glossary-group-title">{group.title}</h3>

              <dl className="glossary-list">
                {group.items.map(({ key, label }) => {
                  const paras = paragraphsFor(key);
                  if (!paras.length) return null;

                  return (
                    <div className="glossary-row" key={key}>
                      <dt className="glossary-term">{label}</dt>
                      <dd className="glossary-def">
                        {paras.map((p, idx) => (
                          <p key={`${key}-${idx}`}>{p}</p>
                        ))}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}
        </section>

        <section id="faq" className="glossary-faq-section" aria-labelledby="faq-title">
          <h2 id="faq-title">FAQ</h2>

          <div className="faq">
            <h3>Where does the data come from?</h3>
            <p>
              StockVisualize aggregates market and fundamentals data and visualises it so you can
              explore trends quickly. Always cross-check important figures with primary sources
              (company filings and your broker) before acting.
            </p>

            <h3>Is this financial advice?</h3>
            <p>
              No. StockVisualize is an educational research tool and does not provide investment,
              tax, or legal advice.
            </p>

            <h3>Why do some metrics look “too high” or “too low”?</h3>
            <p>
              Ratios can vary massively by sector, accounting treatment, and one-off items. Use
              comparisons within the same industry, and look at multi-year trends rather than one
              snapshot.
            </p>

            <h3>What does “higher is better” actually mean?</h3>
            <p>
              It’s a rule of thumb. Higher liquidity/profit margins are often preferable, but
              extremes can be a red flag (e.g., too much idle cash). Context matters.
            </p>

            <h3>Why can valuation ratios be misleading?</h3>
            <p>
              Valuation ratios depend on the denominator (earnings/cash flow/sales). If earnings are
              temporarily depressed or inflated, P/E can look misleadingly high or low. Use multiple
              valuation lenses.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
